import { prefersReducedMotion } from "../utils/prefers-reduced-motion";

/**
 * Click-to-toggle playback for ambient video. Works with either a native
 * <video> (file source) or a Vimeo background iframe (url source). The whole
 * video is the click target; the corner icon reflects play/pause state.
 */
export default class AmbientVideo {
  constructor(rootEl) {
    this.root = rootEl;
    this.toggle = rootEl.querySelector(".ambient-video__toggle");
    this.video = rootEl.querySelector("video");
    this.iframe = rootEl.querySelector(".ambient-video__iframe");

    if (!this.toggle || (!this.video && !this.iframe)) return;

    this.reduceMotion = prefersReducedMotion();
    this.handleToggle = this.handleToggle.bind(this);
    this.syncUI = this.syncUI.bind(this);
    this.toggle.addEventListener("click", this.handleToggle);

    if (this.video) {
      this.initVideo();
    } else {
      this.initVimeo();
    }
  }

  initVideo() {
    this.video.addEventListener("play", () => {
      // The autoplay attribute can fire after init; block it under reduced
      // motion until the user explicitly opts in.
      if (this.reduceMotion && !this.userInitiated) {
        this.video.pause();
        return;
      }
      this.syncUI(false);
    });
    this.video.addEventListener("pause", () => this.syncUI(true));
    if (this.reduceMotion) this.video.pause();
    this.syncUI(this.video.paused);
  }

  async initVimeo() {
    const { default: Player } = await import("@vimeo/player");
    this.player = new Player(this.iframe);
    this.player.on("play", () => this.syncUI(false));
    this.player.on("pause", () => this.syncUI(true));
    await this.player.ready();
    this.applyVideoAspectRatio();
    if (this.reduceMotion) this.player.pause().catch(() => {});
    this.syncUI(this.reduceMotion);
  }

  /**
   * Size the iframe to the video's native aspect ratio so the cover math in CSS
   * fills the container on the video's smallest dimension instead of assuming
   * 16:9 (which letterboxes portrait videos). `--video-ar` is video width/height.
   */
  async applyVideoAspectRatio() {
    try {
      const [width, height] = await Promise.all([
        this.player.getVideoWidth(),
        this.player.getVideoHeight(),
      ]);
      if (width && height) {
        this.iframe.style.setProperty("--video-ar", width / height);
      }
    } catch {
      // Keep the 16:9 CSS fallback if dimensions are unavailable.
    }
  }

  handleToggle(e) {
    // Self-contained: don't trigger an enclosing link/card.
    e.preventDefault();
    e.stopPropagation();

    if (this.video) {
      if (this.video.paused) {
        this.userInitiated = true;
        this.video.play().catch(() => {});
      } else {
        this.video.pause();
      }
    } else if (this.player) {
      this.player
        .getPaused()
        .then((paused) => (paused ? this.player.play() : this.player.pause()));
    }
  }

  syncUI(paused) {
    this.root.classList.toggle("is-paused", paused);
    this.toggle.setAttribute("aria-pressed", paused ? "false" : "true");
    this.toggle.setAttribute(
      "aria-label",
      paused ? "Play video" : "Pause video",
    );
  }
}
